import React from 'react'

import config from '../../config'

import {useAppDispatch, useAppSelector} from '../../hooks/redux'
import {openApiKeyModal} from '../../redux/features/modal'

import {formatDateTime} from '../../libs/formatter'
import * as userApi from '../../services/api/user'
import {captureException} from '../../services/logging'

import keyIcon from '../../../../commons/src/assets/icons/key.svg'

import Button from '../../../../commons/src/components/button'
import FieldNote from '../../../../commons/src/components/field-note'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import InputField from '../../../../commons/src/components/input-field'
import Link from '../../../../commons/src/components/link'
import Paragraph from '../../../../commons/src/components/paragraph'
import Section from '../../../../commons/src/components/section'
import Stack from '../../../../commons/src/components/stack'

const PARTNER_API_DOCS_URL = config.services.partner.baseUrl + '/docs'

const SettingsApiKeyPartial = () => {
  const dispatch = useAppDispatch()
  const user = useAppSelector(state => state.auth.user)
  const [error, setError] = React.useState<string | null>(null)
  const [apiKeyExpiresAt, setApiKeyExpiresAt] = React.useState<string | null>(null)

  const fetchApiKeyExpiration = async () => {
    try {
      const userId = user?.attributes.sub

      if (!userId) {
        throw new Error('User session not available.')
      }

      const userRes = await userApi.getUserPermissions(userId)
      setApiKeyExpiresAt(userRes.apiKeyExpiresAt ? userRes.apiKeyExpiresAt.toString() : null)
    } catch (err) {
      captureException(err)
      setError('Failed to load user data. Try again later.')
    }
  }

  React.useEffect(() => {
    fetchApiKeyExpiration()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleClick = () => {
    setError(null)
    dispatch(
      openApiKeyModal({
        onError: setError,
        onSuccess: fetchApiKeyExpiration,
      })
    )
  }

  const expiresDate = apiKeyExpiresAt ? new Date(apiKeyExpiresAt) : null
  const hasApiKeyExpired = expiresDate ? expiresDate <= new Date() : false

  const getExpiryText = () => {
    if (!expiresDate) {
      return
    }
    if (hasApiKeyExpired) {
      return `Expired on ${formatDateTime(expiresDate.toString())}.`
    } else {
      return `Expires on ${formatDateTime(expiresDate.toString())}.`
    }
  }

  return (
    <>
      <Section>
        <Stack>
          <Headline label="Your API Key" className="u-no-margin-bottom" />
          <Paragraph className="u-no-margin-bottom">
            This is your secure credential for accessing our partner API.
            <br />
            Pass the key in the <code>X-API-Key</code> header with each API request.
            <br />
            See our{' '}
            <Link href={PARTNER_API_DOCS_URL} target="_blank">
              API documentation
            </Link>{' '}
            for all available routes.
          </Paragraph>
          {apiKeyExpiresAt && (
            <FieldNote
              appearance={hasApiKeyExpired ? 'error' : undefined}
              note={getExpiryText()}
              className="u-margin-bottom-s"
            >
              <InputField
                disabled
                icon={<Icon source={keyIcon} />}
                value={'•'.repeat(64)}
                className="u-margin-bottom-s"
              />
            </FieldNote>
          )}
          <Stack space="80">
            <Button hierarchy="secondary" onClick={handleClick}>
              {apiKeyExpiresAt ? 'Regenerate API Key' : 'Generate API Key'}
            </Button>
            {error && (
              <Paragraph size="s" error>
                {error}
              </Paragraph>
            )}
          </Stack>
        </Stack>
      </Section>
    </>
  )
}

export default SettingsApiKeyPartial
